import { authedFetch } from "../../utils/authedFetch";
import { useState } from "react";
import { X, Plus, CalendarPlus } from "lucide-react";
import { buildAddToCalendarUrl } from "../../utils/importantDates";
import { useStateValue } from "../../context/StateProvider";
import AgentDropdown from "./agentDropdown";
import StatusDropdown from "./StatusDropdown";
import { storage } from "../../firebase/firebaseConfig";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { FaUserCircle } from "react-icons/fa";

const TaskPopup = ({ task, onClose, onUpdateTask }) => {
  const [{ users }] = useStateValue();

  const [title, setTitle] = useState(task.title || "");
  const [deadline, setDeadline] = useState(task.deadline || "");
  const [status, setStatus] = useState(task.status || "Open");
  const [assignee, setAssignee] = useState(task.assignee || null);
  const [attachments, setAttachments] = useState(task.attachments || []);
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  const allAgents = users.filter(
    (u) =>
      (u.role === "Employee" ||
        u.role === "Admin" ||
        u.role === "Supervisor") &&
      u.type !== 1 &&
      u.type !== 2,
  );

  const updateTaskField = (updatedFields) => {
    const updatedTask = {
      ...task,
      title,
      deadline,
      status,
      attachments,
      assignee: assignee ? { ...assignee } : null,
      ...updatedFields,
    };
    onUpdateTask(updatedTask);
  };

  const handleAddAttachment = async (e) => {
    const files = Array.from(e.target.files);
    for (let file of files) {
      try {
        const fileId = Date.now() + Math.random();
        const storageRef = ref(
          storage,
          `tasks/${task.id}/${fileId}-${file.name}`,
        );
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);

        const newAttachment = {
          id: fileId,
          url,
          name: file.name,
          type: file.type.startsWith("image/") ? "image" : "file",
          path: storageRef.fullPath,
        };

        setAttachments((prev) => {
          const updated = [...prev, newAttachment];
          updateTaskField({ attachments: updated });
          return updated;
        });
      } catch (err) {
        console.log("Upload error:", err);
      }
    }
  };

  const handleRemoveAttachment = async (att) => {
    try {
      if (att?.path) await deleteObject(ref(storage, att.path));
      setAttachments((prev) => {
        const updated = prev.filter((a) => a.id !== att.id);
        updateTaskField({ attachments: updated });
        return updated;
      });
    } catch (err) {
      console.log("Delete error:", err);
    }
  };

  const handleSelectAgent = (agent, type) => {
    if (type === "Unassign" || !agent) {
      setAssignee(null);
      updateTaskField({ assignee: null });
      setShowAssigneeDropdown(false);
      return;
    }

    const minimalAgent = {
      id: agent.id,
      firstname: agent.firstname,
      lastname: agent.lastname,
      profilepicture: agent.profilepicture || "",
      role: agent.role,
    };
    newmessagesuccess(
      "New Task Assigned",
      minimalAgent?.id || 0,
      title || "",
      task?.id || "",
      "newtask",
    );
    setAssignee(minimalAgent);
    updateTaskField({ assignee: minimalAgent });
    setShowAssigneeDropdown(false);
  };

  const newmessagesuccess = async (title, id, message, taskid, type) => {
    try {
      const res = await authedFetch("https://us-central1-team-edelstein.cloudfunctions.net/api/newmessage", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          title: title,
          message: message,
          id: id,
          taskid: taskid,
          dealid: task?.dealid || "", // agar available ho
          type: type,
        }),
      });

      if (res.status === 200) {
        console.log("Successfully Notified User");
      } else {
        console.log("Notification Error");
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/60 z-50" onClick={onClose} />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
        <div className="w-full max-w-md lg:max-w-2xl max-h-[90%] overflow-y-auto bg-white rounded-2xl shadow-2xl">
          {/* Header */}
          <div className="flex justify-between items-center px-6 py-4 bg-[#1E293B] text-white rounded-t-2xl">
            <h2 className="text-lg font-semibold">Task Details</h2>
            <button
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30"
            >
              <X size={18} />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 space-y-5">
            {/* Task Name */}
            <div>
              <label className="text-sm text-gray-600 mb-1 block">
                Task Name
              </label>
              <input
                className="w-full border rounded-lg px-3 py-3.5 text-sm"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onBlur={() => {
                  updateTaskField({ title });

                  newmessagesuccess(
                    title,
                    assignee?.id || 0,
                    "Task title updated",
                    task?.id || "",
                    "title",
                  );
                }}
              />
            </div>

            {/* Assignee & Status (stacked on mobile, 2-cols on desktop) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Assignee */}
              <div className="relative">
                <label className="text-sm text-gray-600 mb-1 block">
                  Assignee
                </label>
                <div
                  className="flex items-center gap-2 border rounded-lg px-3 py-2 cursor-pointer"
                  onClick={() => setShowAssigneeDropdown(!showAssigneeDropdown)}
                >
                  {assignee?.profilepicture ? (
                    <img
                      src={assignee.profilepicture}
                      className="w-5 h-5 rounded-full"
                      alt=""
                    />
                  ) : (
                    <FaUserCircle className="w-5 h-5 text-gray-400" />
                  )}
                  <p className="text-sm">
                    {assignee
                      ? `${assignee.firstname || ""} ${assignee.lastname || ""}`
                      : "Unassigned"}
                  </p>
                </div>

                {showAssigneeDropdown && (
                  <AgentDropdown
                    visible={showAssigneeDropdown}
                    data={allAgents}
                    style={{ width: "100%" }}
                    onSelect={handleSelectAgent}
                    onClose={() => setShowAssigneeDropdown(false)}
                  />
                )}
              </div>

              {/* Status */}
              <div className="relative">
                <label className="text-sm text-gray-600 mb-1 block">
                  Status
                </label>
                <div
                  className="border rounded-lg px-3 py-2 cursor-pointer text-sm text-gray-700"
                  onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                >
                  {status}
                </div>

                {showStatusDropdown && (
                  <StatusDropdown
                    visible={showStatusDropdown}
                    options={["Open", "In Progress", "Completed"]}
                    style={{ top: "60px", left: "0" }}
                    onSelect={(s) => {
                      setStatus(s);
                      newmessagesuccess(
                        title,
                        assignee?.id || 0,
                        `status changed to ${s}`,
                        task?.id || "",
                        "status",
                      );
                      updateTaskField({ status: s });
                      setShowStatusDropdown(false);
                    }}
                    onClose={() => setShowStatusDropdown(false)}
                  />
                )}
              </div>
            </div>

            {/* Deadline */}
            <div>
              <label className="text-sm text-gray-600 mb-1 block">
                Deadline
              </label>
              <input
                type="date"
                className="w-full border rounded-lg px-3 py-2 text-sm"
                value={deadline}
                onChange={(e) => {
                  const value = e.target.value;
                  setDeadline(value);

                  // ✅ Notification
                  newmessagesuccess(
                    title,
                    assignee?.id || 0,
                    `deadline: ${value}`,
                    task?.id || "",
                    "deadline",
                  );

                  updateTaskField({ deadline: value });
                }}
              />
              {deadline && (
                <a
                  href={buildAddToCalendarUrl({
                    title: `Task: ${title || "Untitled Task"}`,
                    isoDate: deadline,
                    description: task?.description || "",
                  })}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 mt-2 text-xs font-semibold text-linkColor hover:text-themeColor"
                >
                  <CalendarPlus size={14} /> Add to Google Calendar
                </a>
              )}
            </div>

            {/* Attachments */}
            <div>
              <label className="text-sm text-gray-600 mb-2 block">
                Attachments
              </label>
              <div className="flex flex-wrap gap-3">
                <label className="flex items-center justify-center w-20 h-20 bg-[#1E293B] text-white rounded-lg cursor-pointer">
                  <Plus size={28} />
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    onChange={handleAddAttachment}
                  />
                </label>

                {attachments.map((att) => (
                  <div
                    key={att.id}
                    className="relative w-20 h-20 border rounded-lg overflow-hidden"
                  >
                    {att.type === "image" ? (
                      <img
                        src={att.url}
                        alt=""
                        className="w-full h-full object-cover cursor-pointer"
                        onClick={() => setPreviewImage(att.url)}
                      />
                    ) : (
                      <div 
                           onClick={() =>  window.open(att.url, "_blank")}

                      
                      
                      className="flex items-center justify-center h-full text-xs p-1 text-center">
                        {att.name}
                      </div>
                    )}

                    <div className="absolute top-1 right-1 bg-red-500 rounded-full p-1">
                      <X
                        size={10}
                        className="text-white cursor-pointer"
                        onClick={() => handleRemoveAttachment(att)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Preview Image */}
        {previewImage && (
          <div
            className="fixed inset-0 z-60 bg-black/80 flex items-center justify-center"
            onClick={() => setPreviewImage(null)}
          >
            <img
              src={previewImage}
              alt="Preview"
              className="max-h-[90%] max-w-[90%] rounded-lg shadow-lg"
            />
          </div>
        )}
      </div>
    </>
  );
};

export default TaskPopup;
